import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  SearchIcon, 
  CalendarIcon, 
  ClockIcon, 
  HeartIcon, 
  ArrowLeft, 
  ArrowRight 
} from '../../../public/icons';
import { book, camera, writing, flower, Gift } from '../../../public/images/blog';
import { Link } from 'react-router-dom';
import Pagination from '../../../component/shared/Pagination';
import publicBlogService from '../../../services/publicBlogService';
import { useAuth } from '../../../context/AuthContext';
import { useModal } from '../../../context/ModalContext';
import { useToast } from '../../../context/ToastContext';

const Blog = () => {
  const navigate = useNavigate();
  const { isAuthenticated } = useAuth();
  const { openModal } = useModal();
  const toast = useToast();

  const [activeCategory, setActiveCategory] = useState('All Posts');
  const [blogPosts, setBlogPosts] = useState([]);
  const [featuredPosts, setFeaturedPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [pagination, setPagination] = useState({
    currentPage: 1,
    itemsPerPage: 9,
    totalPages: 1,
    totalItems: 0
  });

  const categories = [
    'All Posts',
    'Memory Keeping', 
    'Gift giving',
    'DIY & Craft',
    'Stories'
  ];

  useEffect(() => {
    fetchBlogs();
    fetchFeaturedPosts();
  }, [activeCategory, pagination.currentPage, searchQuery]);

  const fetchBlogs = async () => {
    try {
      setLoading(true);
      const params = {
        page: pagination.currentPage,
        limit: pagination.itemsPerPage,
        category: activeCategory !== 'All Posts' ? activeCategory : undefined,
        search: searchQuery || undefined
      };

      const response = await publicBlogService.getBlogs(params);
      
      if (response.success) {
        setBlogPosts(response.data.blogs);
        setPagination(prev => ({
          ...prev,
          totalPages: response.data.pagination.pages,
          totalItems: response.data.pagination.total
        }));
      }
    } catch (error) {
      console.error('Error fetching blogs:', error);
      toast.error('Failed to load blog posts');
    } finally {
      setLoading(false);
    }
  };

  const fetchFeaturedPosts = async () => {
    try {
      const response = await publicBlogService.getFeaturedBlogs();
      if (response.success) {
        setFeaturedPosts(response.data);
      }
    } catch (error) {
      console.error('Error fetching featured posts:', error);
    }
  };

  const handleLike = async (postId, e) => {
    e.preventDefault();
    e.stopPropagation();

    if (!isAuthenticated) {
      toast.error('Please log in to like posts');
      openModal('signin');
      return;
    }

    try {
      const response = await publicBlogService.likeBlog(postId);
      if (response.success) {
        // Update the like count in the UI
        setBlogPosts(prev => prev.map(post => 
          post._id === postId ? { ...post, likes: response.likes } : post
        ));
        toast.success('Post liked!');
      }
    } catch (error) {
      console.error('Error liking post:', error);
      toast.error('Failed to like post');
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPagination(prev => ({ ...prev, currentPage: 1 }));
    fetchBlogs();
  };

  const handlePageChange = (pageNumber) => {
    setPagination(prev => ({ ...prev, currentPage: pageNumber }));
  };

  const filteredPosts = blogPosts;

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="bg-[#FCF7EE] py-14">
        <div className="container mx-auto px-4 max-w-7xl">
          <div>
            <h1 className="text-5xl md:text-6xl font-bold text-[#1C2A46] mb-6 text-left">
              Kapta Blog
            </h1>
            <p className="text-xl text-[#6B635D] mb-12 text-left font-normal">
              Stories, ideas, and inspiration for preserving life's most <br /> meaningful moments
            </p>
            
            <div className='flex flex-col lg:flex-row justify-between items-start lg:items-center gap-6 mb-8'>
              <div className="flex flex-wrap gap-8 border-b border-[#E4E4E4]">
                {categories.map((category) => (
                  <button
                    key={category}
                    onClick={() => {
                      setActiveCategory(category);
                      setPagination(prev => ({ ...prev, currentPage: 1 }));
                    }}
                    className={`text-base font-medium transition-all duration-200 relative pb-2 ${
                      activeCategory === category
                        ? 'text-[#1C2A46]'
                        : 'text-[#6B635D] hover:text-[#1C2A46] font-normal'
                    }`}
                  >
                    {category}
                    {activeCategory === category && (
                      <div className="absolute bottom-0 left-0 w-full h-[2px] bg-[#1C2A46]"></div>
                    )}
                  </button>
                ))}
              </div>
              
              <form onSubmit={handleSearch} className="relative">
                <input
                  type="text"
                  placeholder="search"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="p-3 w-full md:w-[300px] lg:w-[466px] pr-12 border border-[#DBDBDB] placeholder:text-[#6B635D] placeholder:text-base placeholder:font-normal bg-transparent focus:outline-none focus:ring-2 focus:ring-[#D4A574] focus:border-transparent"
                />
                <button type="submit" className="absolute right-3 top-1/2 transform -translate-y-1/2">
                  <SearchIcon size={24} />
                </button>
              </form>
            </div>
          </div>
        </div>
      </section>

      {/* Featured Posts */}
      {featuredPosts.length > 0 && (
        <section className="bg-[#FCF7EE] py-8">
          <div className="container mx-auto px-4 max-w-7xl">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-16">
              {featuredPosts.map((post) => (
                <Link to={`/blog/${post.slug}`} key={post._id} className="group cursor-pointer">
                  <div className="mb-6">
                    <img
                      src={post.featuredImage}
                      alt={post.title}
                      className="w-full h-[400px] object-cover"
                    />
                  </div>
                  
                  <div className="flex items-center gap-4 mb-4">
                    <span className="px-3 py-1 bg-[#55000C] text-[#FCF7EE] text-sm font-normal">
                      Featured
                    </span>
                  </div>
                  
                  <h3 className="text-xl font-medium text-[#1C2A46] mb-4 text-left line-clamp-1">
                    {post.title}
                  </h3>
                  <p className="text-[#6B635D] mb-6 text-left text-base font-light line-clamp-2">
                    {post.excerpt}
                  </p>
                  
                  <div className="flex items-center justify-between pt-4 border-t border-[#E4E4E4]">
                    <div className="flex items-center gap-8 text-[#6B635D] text-sm">
                      <span className="flex items-center gap-2">
                        <CalendarIcon size={16} />
                        {new Date(post.createdAt).toLocaleDateString('en-US', { 
                          year: 'numeric', 
                          month: 'short', 
                          day: 'numeric' 
                        })}
                      </span>
                      <span className="flex items-center gap-2">
                        <ClockIcon size={16} />
                        {post.readTime}
                      </span>
                    </div>
                    
                    <div className="flex items-center gap-4">
                      <button 
                        onClick={(e) => handleLike(post._id, e)}
                        className="flex items-center gap-2 text-[#6B635D] hover:text-[#D4A574] transition-colors"
                      >
                        <HeartIcon size={18} />
                        <span className="text-sm">{post.likes}</span>
                      </button>
                      <span className="text-[#1C2A46] font-medium hover:text-[#D4A574] transition-colors text-sm flex items-center gap-1">
                        Read more
                        <ArrowRight className="w-4 h-4" />
                      </span>
                    </div>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Blog Posts Grid */}
      <section className="bg-[#FCF7EE] py-8">
        <div className="container mx-auto px-4 max-w-7xl">
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#55000C]"></div>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                {filteredPosts.map((post) => (
                  <Link to={`/blog/${post.slug}`} key={post._id} className="group cursor-pointer">
                    <div className="overflow-hidden mb-4">
                      <img
                        src={post.featuredImage}
                        alt={post.title}
                        className="w-full object-cover transition-transform duration-500 group-hover:scale-105"
                      />
                    </div>
                    
                    <div className="flex items-center justify-between mb-3">
                      <span className="p-1.5 border border-bordercolor text-[#1C2A46] text-xs font-normal">
                        {post.category}
                      </span>
                    </div>
                    
                    <h3 className="text-lg font-medium text-[#1C2A46] mb-3 text-left line-clamp-1">
                      {post.title}
                    </h3>
                    <p className="text-[#6B635D] mb-4 text-left text-sm font-light line-clamp-2">
                      {post.excerpt}
                    </p>
                    
                    <div className="">
                      <div className="flex items-center justify-between gap-8 text-[#6B635D] text-sm pb-2.5">
                        <span className="flex items-center gap-2">
                          <CalendarIcon size={14} />
                          {new Date(post.createdAt).toLocaleDateString('en-US', { 
                            year: 'numeric', 
                            month: 'short', 
                            day: 'numeric' 
                          })}
                        </span>
                        <span className="flex items-center gap-2">
                          <ClockIcon size={14} />
                          {post.readTime}
                        </span>
                      </div>
                      
                      <div className="flex items-center pt-4 justify-between gap-4 border-t border-[#E4E4E4]">
                        <button 
                          onClick={(e) => handleLike(post._id, e)}
                          className="flex items-center gap-1 text-[#6B635D] hover:text-[#D4A574] transition-colors"
                        >
                          <HeartIcon size={16} />
                          <span className="text-sm">{post.likes}</span>
                        </button>
                        <span className="text-[#1C2A46] font-medium hover:text-[#D4A574] transition-colors text-sm flex items-center gap-1">
                          Read more
                          <ArrowRight className="w-4 h-4" />
                        </span>
                      </div>
                    </div>
                  </Link>
                ))}
              </div>
              
              {/* Pagination */}
              {pagination.totalPages > 1 && (
                <Pagination 
                  currentPage={pagination.currentPage}
                  totalPages={pagination.totalPages}
                  totalItems={pagination.totalItems}
                  itemsPerPage={pagination.itemsPerPage}
                  onPageChange={handlePageChange}
                />
              )}
            </>
          )}
        </div>
      </section>
    </div>
  );
};

export default Blog;